import React, {useCallback, useState} from "react";
import {Button, Form, Input, message, theme, Typography} from "antd";
import {BankOutlined, ExclamationCircleOutlined, MailOutlined, UserOutlined} from "@ant-design/icons";
import {useCreateOrganisationWithUserMutation} from "../services/nestjs-base-api";
import {ICreateOrganisationWithApiUser} from "../services/nestjs-base-api/types";
import {FormattedMessage} from "react-intl";
import {useIntl} from "react-intl";
import AuthLayout from "../components/AuthLayout";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const { useToken } = theme;
const { Text, Link } = Typography;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 24px;
  }
  
  .ant-input-affix-wrapper,
  .ant-input {
    height: 48px;
    font-size: 16px;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    
    &:hover,
    &:focus {
      border-color: #4169e1;
      box-shadow: 0 0 0 2px rgba(65, 105, 225, 0.1);
    }
  }
  
  .ant-input-prefix {
    color: #4169e1;
    font-size: 18px;
    margin-right: 12px;
  }
`;

const StyledButton = styled(Button)`
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(65, 105, 225, 0.4);
  }
  
  &:disabled {
    background: #f5f5f5;
    box-shadow: none;
    transform: none;
  }
`;

const FooterWrapper = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: #4169e1;
  font-weight: 500;
  
  &:hover {
    color: #1890ff;
  }
`;

const SignupPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const { token } = useToken();
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();
    const [hideForm, setHideForm] = useState<boolean>(false);
    const [register, registerResult] = useCreateOrganisationWithUserMutation();

    const onRegisterError = useCallback(() => {
        messageApi.open({
            type: 'error',
            content: <FormattedMessage id='signUp.failed' />,
            className: 'custom-class',
            style: {
                marginTop: '1vh',
                fontSize: '1.2rem'
            },
            duration: 6,
            icon: <ExclamationCircleOutlined style={{fontSize: '1.2rem', paddingBottom: '0.1rem'}} />
        });
    }, [messageApi]);

    const handleRegister = useCallback(async (values: any) => {
        const payload = {
            organisationName: values.organisationName,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
        } as ICreateOrganisationWithApiUser;

        const registerResponse = await register({payload: payload});

        if ('data' in registerResponse) {
            setHideForm(true);
        } else {
            onRegisterError();
        }
    }, [onRegisterError, register]);


    return (
        <>
            {contextHolder}
            <AuthLayout
                title={hideForm 
                    ? intl.formatMessage({ id: 'signUp.success.title' }) 
                    : intl.formatMessage({ id: 'signUp.title' })
                }
                subtitle={hideForm 
                    ? intl.formatMessage({ id: 'signUp.success.welcome' }) 
                    : intl.formatMessage({ id: 'signUp.welcome' })
                }
            >
                <div style={{display: hideForm ? 'none' : 'block'}}>
                    <StyledForm
                        name="normal_register"
                        onFinish={handleRegister}
                        layout="vertical"
                        requiredMark="optional"
                    >
                        <Form.Item
                            name="firstName"
                            rules={[
                                {
                                    type: "string",
                                    required: true,
                                    message: intl.formatMessage({ id: 'signUp.form.firstName.missing' }),
                                },
                            ]}
                        >
                            <Input
                                prefix={<UserOutlined/>}
                                placeholder={intl.formatMessage({ id: 'signUp.form.firstName' })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="lastName"
                            rules={[
                                {
                                    type: "string",
                                    required: true,
                                    message: intl.formatMessage({ id: 'signUp.form.lastName.missing' }),
                                },
                            ]}
                        >
                            <Input
                                prefix={<UserOutlined/>}
                                placeholder={intl.formatMessage({ id: 'signUp.form.lastName' })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="email"
                            rules={[
                                {
                                    type: "email",
                                    required: true,
                                    message: intl.formatMessage({ id: 'signUp.form.email.missing' }),
                                },
                            ]}
                        >
                            <Input
                                prefix={<MailOutlined/>}
                                placeholder={intl.formatMessage({ id: 'signUp.form.email' })}
                            />
                        </Form.Item>
                        <Form.Item
                            name="organisationName"
                            rules={[
                                {
                                    type: "string",
                                    required: true,
                                    message: intl.formatMessage({ id: 'signUp.form.organisation.missing' }),
                                },
                            ]}
                        >
                            <Input
                                prefix={<BankOutlined/>}
                                placeholder={intl.formatMessage({ id: 'signUp.form.organisation' })}
                            />
                        </Form.Item>
                        <Form.Item>
                            <StyledButton 
                                block={true} 
                                type="primary" 
                                htmlType="submit"
                                disabled={registerResult.isLoading}
                                loading={registerResult.isLoading}
                            >
                                <FormattedMessage id='signUp.form.submit' />
                            </StyledButton>
                            <FooterWrapper>
                                <Text style={{ color: token.colorTextSecondary }}>
                                    <FormattedMessage id='signUp.haveAccount' />
                                </Text>{" "}
                                <StyledLink onClick={() => navigate('/sign-in')}>
                                    <FormattedMessage id='signUp.signIn' />
                                </StyledLink>
                            </FooterWrapper>
                        </Form.Item>
                    </StyledForm>
                </div>
                <div style={{display: hideForm ? 'block' : 'none'}}>
                    {/* Success message is handled by the title/subtitle in AuthLayout */}
                </div>
            </AuthLayout>
        </>
    );
}

export default SignupPage;
